import { useState } from 'react';
import { Plus, Download, FileText, TrendingUp, TrendingDown, Building2 } from 'lucide-react';
import { useFunding, type CommitmentWithDetails, type FundingCategory, type FundingSourceType } from '../../hooks/useFunding';
import { FundingCommitmentModal } from './FundingCommitmentModal';
import { FundingDisbursementModal } from './FundingDisbursementModal';
import { FundingCommitmentDetailModal } from './FundingCommitmentDetailModal';

interface SectionConfig {
  title: string;
  category: FundingCategory;
  sourceType?: FundingSourceType;
  color: string;
}

const SECTIONS: SectionConfig[] = [
  { title: 'Public Fonctionnement', category: 'operating', sourceType: 'public', color: 'bg-orange-50 border-orange-200' },
  { title: 'Public Investissement', category: 'investment', sourceType: 'public', color: 'bg-orange-50 border-orange-200' },
  { title: 'Prive Fonctionnement', category: 'operating', sourceType: 'private', color: 'bg-blue-50 border-blue-200' },
  { title: 'Prive Investissement', category: 'investment', sourceType: 'private', color: 'bg-blue-50 border-blue-200' },
  { title: 'Fonds propres', category: 'equity', color: 'bg-gray-50 border-gray-200' },
  { title: 'Prets', category: 'loan', color: 'bg-yellow-50 border-yellow-200' }
];

export function FundingDashboard() {
  const { commitments, loading, getCommitmentsByCategory, getTotalsByCategory, getTotalsBySourceType, refresh } = useFunding();
  const [showCommitmentModal, setShowCommitmentModal] = useState(false);
  const [showDisbursementModal, setShowDisbursementModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedCommitment, setSelectedCommitment] = useState<CommitmentWithDetails | null>(null);
  const [editingCommitment, setEditingCommitment] = useState<CommitmentWithDetails | null>(null);

  const handleCloseCommitmentModal = () => {
    setShowCommitmentModal(false);
    setEditingCommitment(null);
    refresh();
  };

  const handleCloseDisbursementModal = () => {
    setShowDisbursementModal(false);
    setSelectedCommitment(null);
    refresh();
  };

  const handleCloseDetailModal = () => {
    setShowDetailModal(false);
    setSelectedCommitment(null);
    refresh();
  };

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (date?: string) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('fr-FR');
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      planned: 'Prevu',
      promised: 'Promis',
      signed: 'Signe',
      in_progress: 'En cours',
      paid: 'Verse',
      cancelled: 'Annule'
    };
    return labels[status] || status;
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      planned: 'bg-gray-100 text-gray-700',
      promised: 'bg-blue-100 text-blue-700',
      signed: 'bg-emerald-100 text-emerald-700',
      in_progress: 'bg-yellow-100 text-yellow-700',
      paid: 'bg-green-100 text-green-700',
      cancelled: 'bg-red-100 text-red-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  const handleAddDisbursement = (commitment: CommitmentWithDetails) => {
    setSelectedCommitment(commitment);
    setShowDisbursementModal(true);
  };

  const handleEditCommitment = (commitment: CommitmentWithDetails) => {
    setEditingCommitment(commitment);
    setShowCommitmentModal(true);
  };

  const handleViewDetails = (commitment: CommitmentWithDetails) => {
    setSelectedCommitment(commitment);
    setShowDetailModal(true);
  };

  const totals = getTotalsByCategory();
  const totalsBySourceType = getTotalsBySourceType();

  const grandTotal = {
    committed: Object.values(totals).reduce((sum, t) => sum + t.committed, 0),
    paid: Object.values(totals).reduce((sum, t) => sum + t.paid, 0),
    remaining: Object.values(totals).reduce((sum, t) => sum + t.remaining, 0)
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Engagements Financeurs</h2>
          <p className="text-sm text-gray-600 mt-1">Suivi des subventions, dons et prets</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowCommitmentModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Nouvel engagement
          </button>
          <button className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
            <Download className="w-4 h-4" />
            Exporter
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-100 rounded-lg">
              <FileText className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Acquis / Promis</p>
              <p className="text-2xl font-bold text-gray-900">{formatAmount(grandTotal.committed)} €</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-emerald-100 rounded-lg">
              <TrendingUp className="w-6 h-6 text-emerald-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Verse</p>
              <p className="text-2xl font-bold text-gray-900">{formatAmount(grandTotal.paid)} €</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-orange-100 rounded-lg">
              <TrendingDown className="w-6 h-6 text-orange-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Reste a verser</p>
              <p className="text-2xl font-bold text-gray-900">{formatAmount(grandTotal.remaining)} €</p>
            </div>
          </div>
        </div>
      </div>

      {/* Sections */}
      {SECTIONS.map(section => {
        const sectionCommitments = getCommitmentsByCategory(section.category, section.sourceType);
        if (sectionCommitments.length === 0) return null;

        const sectionTotal = sectionCommitments.reduce((sum, c) => sum + parseFloat(c.commitment_amount.toString()), 0);
        const sectionPaid = sectionCommitments.reduce((sum, c) => sum + c.paid_total, 0);
        const sectionRemaining = sectionCommitments.reduce((sum, c) => sum + c.remaining, 0);

        return (
          <div key={`${section.category}-${section.sourceType}`} className={`border-2 rounded-lg ${section.color} overflow-hidden`}>
            <div className="bg-white bg-opacity-60 px-4 py-3 border-b-2 border-inherit">
              <h3 className="font-bold text-gray-900">{section.title}</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-white bg-opacity-40">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Financeur</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700">Acquis/Promis</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700">Verse</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Date(s) versement</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700">Reste a verser</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Periode</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Affectation</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Etat</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {sectionCommitments.map(commitment => {
                    const lastDisbursement = commitment.disbursements[0];
                    const multiplePayments = commitment.disbursements.length > 1;

                    return (
                      <tr key={commitment.id} className="bg-white hover:bg-gray-50 cursor-pointer" onClick={() => handleViewDetails(commitment)}>
                        <td className="px-4 py-3">
                          <div>
                            <p className="font-medium text-gray-900">{commitment.source?.name || 'N/A'}</p>
                            <p className="text-xs text-gray-500">{commitment.title}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right font-medium">{formatAmount(parseFloat(commitment.commitment_amount.toString()))} €</td>
                        <td className="px-4 py-3 text-right text-emerald-600 font-medium">{formatAmount(commitment.paid_total)} €</td>
                        <td className="px-4 py-3">
                          {lastDisbursement ? (
                            <span className="text-sm">
                              {formatDate(lastDisbursement.paid_date)}
                              {multiplePayments && <span className="text-xs text-gray-500 ml-1">(+{commitment.disbursements.length - 1})</span>}
                            </span>
                          ) : '-'}
                        </td>
                        <td className="px-4 py-3 text-right text-orange-600 font-medium">{formatAmount(commitment.remaining)} €</td>
                        <td className="px-4 py-3 text-sm">
                          {commitment.convention_start_date && commitment.convention_end_date ? (
                            <span>{formatDate(commitment.convention_start_date)} - {formatDate(commitment.convention_end_date)}</span>
                          ) : '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">{commitment.allocation || '-'}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(commitment.status)}`}>
                            {getStatusLabel(commitment.status)}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAddDisbursement(commitment);
                            }}
                            className="text-sm text-emerald-600 hover:text-emerald-700 font-medium"
                          >
                            + Versement
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                  <tr className="bg-gray-50 font-bold">
                    <td className="px-4 py-3">TOTAL {section.title}</td>
                    <td className="px-4 py-3 text-right">{formatAmount(sectionTotal)} €</td>
                    <td className="px-4 py-3 text-right text-emerald-600">{formatAmount(sectionPaid)} €</td>
                    <td className="px-4 py-3"></td>
                    <td className="px-4 py-3 text-right text-orange-600">{formatAmount(sectionRemaining)} €</td>
                    <td colSpan={4}></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        );
      })}

      {/* Grand Total */}
      <div className="bg-gray-800 text-white rounded-lg p-6">
        <div className="grid grid-cols-4 gap-4">
          <div>
            <p className="text-sm text-gray-300">Total General</p>
            <p className="text-2xl font-bold">{formatAmount(grandTotal.committed)} €</p>
          </div>
          <div>
            <p className="text-sm text-gray-300">Total Fonctionnement</p>
            <p className="text-xl font-bold">{formatAmount(totals.operating.committed)} €</p>
          </div>
          <div>
            <p className="text-sm text-gray-300">Total Investissement</p>
            <p className="text-xl font-bold">{formatAmount(totals.investment.committed)} €</p>
          </div>
          <div>
            <p className="text-sm text-gray-300">Total Fonds Propres</p>
            <p className="text-xl font-bold">{formatAmount(totals.equity.committed)} €</p>
          </div>
        </div>
      </div>

      {/* Modals */}
      {showCommitmentModal && (
        <FundingCommitmentModal
          commitment={editingCommitment || undefined}
          onClose={handleCloseCommitmentModal}
        />
      )}

      {showDisbursementModal && selectedCommitment && (
        <FundingDisbursementModal
          commitment={selectedCommitment}
          onClose={handleCloseDisbursementModal}
        />
      )}

      {showDetailModal && selectedCommitment && (
        <FundingCommitmentDetailModal
          commitment={selectedCommitment}
          onClose={handleCloseDetailModal}
          onEdit={() => {
            setShowDetailModal(false);
            handleEditCommitment(selectedCommitment);
          }}
        />
      )}
    </div>
  );
}
